import React, { useEffect, useState } from 'react';
import { useParams, Link, useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import { Helmet } from 'react-helmet-async';
import { ArrowLeft, CheckCircle2 } from 'lucide-react';
import { fetchCollection } from '../lib/cms';
import { colors } from '../data';
import { getLocalized } from '../lib/utils';

const ServiceDetail = () => {
  const { slug } = useParams();
  const navigate = useNavigate();
  const [service, setService] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchCollection('services').then((data) => {
      const found = data.find((s) => getLocalized(s.path) === slug);
      if (found) {
        setService(found);
      } else {
        navigate('/ydelser');
      }
      setLoading(false);
    });
  }, [slug, navigate]);

  if (loading) return <div className="min-h-screen flex items-center justify-center">Indlæser...</div>;
  if (!service) return null;

  const benefits = String(getLocalized(service.benefits) || '')
    .replace(/\\n/g, '\n')
    .split(/\n|,/)
    .map((benefit) => benefit.trim())
    .filter(Boolean);

  return (
    <div className="pb-24">
      <Helmet>
        <title>{getLocalized(service.title)} - Meyer Management ApS</title>
        <meta name="description" content={getLocalized(service.summary || service.description || '')} />
        <html lang="da-DK" />
      </Helmet>

      {/* Hero */}
      <section className="relative h-[60vh] flex items-center justify-center overflow-hidden">
        <img
          src={service.image?.[0]}
          alt={getLocalized(service.title)}
          className="absolute inset-0 w-full h-full object-cover"
        />
        <div className="absolute inset-0 bg-black/40"></div>
        <div className="relative z-10 text-center px-4">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
          >
            <Link
              to="/ydelser"
              className="inline-flex items-center text-white/80 hover:text-white mb-6 transition-colors"
            >
              <ArrowLeft size={18} className="mr-2" /> Tilbage til oversigt
            </Link>
            <h1 className="text-4xl md:text-6xl font-serif font-bold text-white mb-4">
              {getLocalized(service.title)}
            </h1>
          </motion.div>
        </div>
      </section>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 mt-20">
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-16">
          {/* Main Content */}
          <div className="lg:col-span-2">
            <div
              className={`prose prose-lg max-w-none text-stone-600 leading-relaxed font-sans prose-headings:font-serif prose-headings:${colors.contrast}`}
              dangerouslySetInnerHTML={{ __html: getLocalized(service.content) }}
            />

            <div className="mt-16 p-8 md:p-12 bg-stone-50 border border-stone-100">
              <h3 className={`text-2xl font-serif font-bold ${colors.contrast} mb-8`}>
                Det får du ud af forløbet
              </h3>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                {benefits.map((benefit) => (
                  <div key={benefit} className="flex items-start space-x-3">
                    <CheckCircle2 className={`${colors.gold} mt-1 flex-shrink-0`} size={20} />
                    <span className="text-stone-600">{benefit}</span>
                  </div>
                ))}
              </div>
            </div>
          </div>

          {/* Sidebar CTA */}
          <div className="lg:col-span-1">
            <div className={`sticky top-32 p-8 ${colors.accent}/30 border border-stone-100`}>
              <h3 className={`text-xl font-serif font-bold ${colors.contrast} mb-4`}>
                Interesseret i {getLocalized(service.title).toLowerCase()}?
              </h3>
              <p className="text-stone-600 text-sm mb-8 leading-relaxed">
                Lad os tage en uforpligtende dialog om, hvordan jeg kan hjælpe dig eller din virksomhed videre.
              </p>
              <Link
                to="/kontakt"
                className={`block w-full text-center py-4 ${colors.contrastBg} text-white font-medium hover:opacity-90 transition-all shadow-lg shadow-stone-200`}
              >
                Book en samtale
              </Link>
              <div className="mt-8 pt-8 border-t border-stone-200">
                <p className="text-xs text-stone-400 uppercase tracking-widest mb-4">Kontakt Pia direkte</p>
                <p className={`text-sm font-bold ${colors.contrast}`}>+45 22 86 35 55</p>
                <p className="text-sm text-stone-600">hej@piameyer.dk</p>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ServiceDetail;
